import { useMutation } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Avatar, Grid } from '@mui/material';
import { useContext } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  RemoveProfilePictureDocument,
  UpdateUserProfileDocument,
  GetCurrentUserDocument,
} from '@/graphql/generated/graphql';
import useDirectUpload from '@/hooks/useDirectUpload';
import { BLUE } from '@/theme/palette';
import { displayNotification } from '@/utils/notifications';

const ProfilePictureSection: React.FC = () => {
  const { currentUser } = useContext(CurrentUserContext);
  const { isUploading, uploadFile } = useDirectUpload();

  const [removePicture, { loading: loadingRemovePic }] = useMutation(RemoveProfilePictureDocument);
  const [updateProfile] = useMutation(UpdateUserProfileDocument);

  const handleRemoveImage = () => {
    removePicture({
      variables: { id: currentUser?.id as string },
      onCompleted: () => {
        displayNotification({
          type: 'success',
          text: 'Profile picture removed successfully',
        });
      },
      onError: (error) => {
        displayNotification({
          type: 'error',
          text: error.message,
        });
      },
      refetchQueries: ['getCurrentUser'],
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      await uploadFile(file, 'profile-images').then((response) => {
        event.target.value = '';

        updateProfile({
          variables: {
            user: {
              id: currentUser?.id as string,
              imageSignedId: response?.signedBlobId as string,
            },
          },
          refetchQueries: [GetCurrentUserDocument],
        });
      });
    }
  };

  return (
    <Grid
      container
      justifyContent="flex-start"
      alignItems="center"
      size={{
        xs: 3,
        lg: 2,
      }}
    >
      <Box sx={{ position: 'relative', width: 160, height: 160 }}>
        {/* Avatar */}
        <Avatar
          src={currentUser?.imageUrl as string}
          sx={{
            width: 140,
            height: 140,
            border: `10px solid ${BLUE}`,
            boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;',
          }}
        />

        {/* Edit Icon (bottom-right) */}
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            bottom: 1,
            right: 5,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: 'grey.200',
            },
          }}
          component="label"
        >
          <EditIcon fontSize="small" />
          <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={isUploading} />
        </IconButton>
        {/* Remove Icon (top-left) */}
        {currentUser?.imageUrl && !loadingRemovePic && (
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'grey.200',
              },
            }}
            onClick={handleRemoveImage}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Grid>
  );
};

export default ProfilePictureSection;
